import { useEffect, useState } from 'react';
import { isDev } from '../../../util/urlHelpers';
import { setUserAvatarFbMethod } from '../../../util/localStorage';

export const INVALID_PERMISSIONS = 'invalid-permissions';

const auth_failed_err_msg =
    'Du hast die Anmeldung abgebrochen oder die Autorisierung wurde nicht vollständig durchgeführt.';

/**
 * the user is logged in and has authenticated your
 * app, and response.authResponse supplies
 * the user's ID, a valid access token, a signed
 * request, and the time the access token
 * and signed request each expire
 */
const getLoginStatus = () =>
    new Promise(async (resolve, reject) => {
        window.FB.getLoginStatus(response => {
            if (response.status === 'connected') {
                resolve(response.authResponse.accessToken);
            } else {
                reject(null);
            }
        });
    });

const getPermissions = necessaryPermissions =>
    new Promise((resolve, reject) => {
        if (!Array.isArray(necessaryPermissions) || necessaryPermissions.length === 0) {
            return resolve();
        }

        const checkPermissions = ({ data }) => {
            /** e.g. an error occured, move to login step then */
            if (!data) {
                return reject();
            }
            /**
             * if there is no a necessary permission inside data (permission granted),
             * throw and move to login step then
             */
            const permissionsGranted = necessaryPermissions.every(np =>
                data.find(({ permission, status }) => status === 'granted' && permission === np)
            );

            if (permissionsGranted) resolve();
            else {
                reject({ reason: INVALID_PERMISSIONS });
            }
        };

        window.FB.api('/me/permissions', checkPermissions);
    });

const loginToFbSdk = (loginParams = {}, strategy) =>
    new Promise(async (resolve, reject) => {
        try {
            const accessToken = await getLoginStatus();

            if (accessToken) {
                const permissionsToCheck = loginParams?.scope ? loginParams.scope.split(',') : null;
                await getPermissions(permissionsToCheck);
                resolve(accessToken);
            }
        } catch (e) {
            const isInvalidPermissions = e && e.reason === INVALID_PERMISSIONS;

            if (isInvalidPermissions) {
                setUserAvatarFbMethod({ strategy });
                return reject(e);
            }

            window.FB.login(response => {
                if (response.authResponse) {
                    resolve(response.authResponse.accessToken);
                } else {
                    /** User cancelled login or did not fully authorize. */
                    reject(null);
                }
            }, loginParams);
        }
    });

export const useFbInitScript = ({ appId, onload, loginParams = {}, strategy }) => {
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [redirectWarning, setRedirectWarning] = useState(false);

    const login = async () => {
        try {
            const accessToken = await loginToFbSdk(loginParams, strategy);

            if (!accessToken) {
                throw new Error('Failed to get access token.');
            }

            onload(accessToken);
        } catch (error) {
            const { message, reason } = error || {};

            if (isDev()) console.log('FB SDK Error: ' + (message || 'Login failed.'));

            const isInvalidPermissions = reason === INVALID_PERMISSIONS;

            if (isInvalidPermissions) {
                setRedirectWarning(true);
            } else {
                setError(message || auth_failed_err_msg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!appId) setError('Failed to initialize the SDK: code 0.');

        /** add initialization script, ... */
        const fbAsyncInitScript = document.createElement('script');
        fbAsyncInitScript.text = `
                window.fbAsyncInit = function() {
                  FB.init({
                    appId            : '${appId}',
                    xfbml            : true,
                    version          : 'v22.0'
                  });
                };
              `;
        document.head.appendChild(fbAsyncInitScript);

        /** ...then, add sdk script */
        const sdkScript = document.createElement('script');
        sdkScript.async = true;
        sdkScript.defer = true;
        sdkScript.src = 'https://connect.facebook.net/en_US/sdk.js';
        sdkScript.onload = login;

        document.head.appendChild(sdkScript);

        return () => {
            document.head.removeChild(fbAsyncInitScript);
            document.head.removeChild(sdkScript);
        };
    }, [appId]);

    return { error, loading, redirectWarning };
};
